import type {
  DealerCustomersGlobalSearchQuery,
  DealerCustomersGlobalSearchQueryVariables,
} from "@/gql/graphql";
import { UserRole } from "@/gql/graphql";
import { getPrimaryOwner } from "@/business-rules/customers";
import { DEALER_CUSTOMERS_GLOBAL_SEARCH_QUERY } from "../global-search-queries";
import type {
  CustomerCompanyOption,
  CustomerRoleOption,
  CustomerSearchItems,
  GlobalSearchApolloClient,
} from "../types";
import { executeGlobalSearchQuery } from "./executeGlobalSearchQuery";

export type DealerCustomerUser =
  DealerCustomersGlobalSearchQuery["dealerCustomers"][number];

type User = NonNullable<NonNullable<DealerCustomerUser["users"]>[number]>;

type DealerUserType = Extract<User, { __typename?: "User" }>;

function isCustomerUser(user: User): user is DealerUserType {
  return user.__typename === "User";
}

function hasOnlyContactRole(role: UserRole | undefined): boolean {
  if (!role) return false;
  return role === UserRole.Contact;
}

function doesUserMatchSearchTerm(
  user: DealerUserType,
  normalizedTerm: string
): boolean {
  const firstName = user.firstName.trim().toLowerCase();
  const lastName = user.lastName.trim().toLowerCase();
  const fullName = `${firstName} ${lastName}`;
  return (
    user.email.toLowerCase().includes(normalizedTerm) ||
    firstName.includes(normalizedTerm) ||
    lastName.includes(normalizedTerm) ||
    fullName.includes(normalizedTerm)
  );
}

function doesSearchTermMatchForRole(
  user: User,
  normalizedTerm: string
): user is DealerUserType {
  if (!isCustomerUser(user)) {
    return false;
  }
  if (hasOnlyContactRole(user.role)) {
    return false;
  }
  return doesUserMatchSearchTerm(user, normalizedTerm);
}

export async function loadDealerCustomersList(
  client: GlobalSearchApolloClient,
  accountId: string,
  signal?: AbortSignal
): Promise<DealerCustomersGlobalSearchQuery["dealerCustomers"]> {
  const data = await executeGlobalSearchQuery<
    DealerCustomersGlobalSearchQuery,
    DealerCustomersGlobalSearchQueryVariables
  >({
    client,
    query: DEALER_CUSTOMERS_GLOBAL_SEARCH_QUERY,
    variables: { accountId },
    signal,
  });

  return data?.dealerCustomers ?? [];
}

export function filterDealerCustomersForSearch(
  customers: DealerCustomersGlobalSearchQuery["dealerCustomers"],
  searchTerm: string
): { totalCount: number; items: CustomerSearchItems } {
  const normalizedTerm = searchTerm.toLowerCase();

  const buildRoleOption = (
    customer: DealerCustomerUser,
    user: DealerUserType
  ): CustomerRoleOption => ({
    customerId: customer.id,
    customer: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
    },
  });

  const companyOptions: CustomerCompanyOption[] = [];
  const roleOptions: CustomerRoleOption[] = [];

  for (const customer of customers) {
    const companyNameMatches = customer.companyName
      .toLowerCase()
      .includes(normalizedTerm);

    const customerUsers = customer.users;

    const userNameMatches = customerUsers.some(
      (user) =>
        isCustomerUser(user) && doesUserMatchSearchTerm(user, normalizedTerm)
    );

    if (companyNameMatches || userNameMatches) {
      companyOptions.push({
        customerId: customer.id,
        companyName: customer.companyName,
        ownerEmail: getPrimaryOwner(customerUsers)?.email ?? null,
      });
    }

    for (const user of customerUsers) {
      if (!doesSearchTermMatchForRole(user, normalizedTerm)) continue;
      roleOptions.push(buildRoleOption(customer, user));
    }
  }

  return {
    totalCount: companyOptions.length + roleOptions.length,
    items: { companyOptions, roleOptions },
  };
}
